import { useEffect, type ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface RiskFundMoveDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  reason: string;
  onReasonChange: (value: string) => void;
  error?: string | null;
  loading?: boolean;
  confirmLabel?: string;
  /**
   * When set, the dialog shows an "Amount to move" field capped at this value.
   * Omit it for bulk moves, which always write off each document's full due.
   */
  maxAmount?: number;
  amount?: string;
  onAmountChange?: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation for moving outstanding balances to the Risk Fund.
 *
 * Unlike a plain `ConfirmDialog` this asks for the write-off reason, which is
 * stored on every `risk_fund_entries` row and is the only explanation a
 * reviewer will have months later. The confirm button stays disabled until a
 * reason is entered.
 */
export function RiskFundMoveDialog({
  open,
  title,
  description,
  reason,
  onReasonChange,
  error,
  loading = false,
  confirmLabel = 'Move to Risk Fund',
  maxAmount,
  amount = '',
  onAmountChange,
  onConfirm,
  onCancel,
}: RiskFundMoveDialogProps) {
  const showAmount = maxAmount !== undefined && onAmountChange !== undefined;

  // Some callers open the dialog while a billing query is still resolving, so
  // the amount can arrive empty. Default it to the full outstanding balance
  // rather than leaving the confirm button inexplicably disabled.
  useEffect(() => {
    if (!open || !showAmount || amount !== '') return;
    if ((maxAmount as number) > 0) {
      onAmountChange!(String(maxAmount));
    }
  }, [open, showAmount, amount, maxAmount, onAmountChange]);

  if (!open) return null;

  const reasonReady = reason.trim().length > 0;

  const amountValue = Number(amount);
  const amountValid =
    !showAmount ||
    (amount.trim() !== '' &&
      Number.isFinite(amountValue) &&
      amountValue > 0 &&
      amountValue <= (maxAmount as number) + 0.001);
  const remaining =
    showAmount && Number.isFinite(amountValue)
      ? Math.max(0, (maxAmount as number) - amountValue)
      : 0;
  const canConfirm = reasonReady && amountValid;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!loading) onCancel();
      }}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg shrink-0 bg-warning/10">
              <AlertTriangle size={20} className="text-warning" />
            </div>
            <div className="space-y-1 min-w-0">
              <h3 className="text-base font-semibold text-text">{title}</h3>
              <div className="text-sm text-text-muted">{description}</div>
            </div>
          </div>

          {showAmount && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor="risk-fund-amount"
                  className="block text-xs font-medium uppercase tracking-wide text-text-muted"
                >
                  Amount to move <span className="text-danger">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => onAmountChange!(String(maxAmount))}
                  disabled={loading}
                  className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                >
                  Full ৳{(maxAmount as number).toLocaleString()}
                </button>
              </div>
              <input
                id="risk-fund-amount"
                type="number"
                min="0.01"
                max={maxAmount}
                step="0.01"
                inputMode="decimal"
                value={amount}
                onChange={(e) => onAmountChange!(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-60"
              />
              <div className="flex items-center justify-between gap-2 text-[11px] text-text-muted">
                <span>
                  Outstanding ৳{(maxAmount as number).toLocaleString()}
                </span>
                <span>
                  Remaining after move ৳{remaining.toLocaleString()}
                </span>
              </div>
              {!amountValid && (
                <p className="text-[11px] text-danger">
                  Enter an amount between ৳0.01 and ৳
                  {(maxAmount as number).toLocaleString()}.
                </p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <label
              htmlFor="risk-fund-reason"
              className="block text-xs font-medium uppercase tracking-wide text-text-muted"
            >
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              id="risk-fund-reason"
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              rows={3}
              maxLength={1000}
              disabled={loading}
              placeholder="e.g. Patient unable to pay — abandoned / deceased / indigent"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none disabled:opacity-60"
            />
            <p className="text-[11px] text-text-muted">
              Stored on the Risk Fund record for audit.
            </p>
          </div>

          <div className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-text-muted">
            The outstanding amount leaves active receivables and cannot be
            collected through normal billing. Deleting the Risk Fund entry later
            reverses the write-off.
          </div>

          {error && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          {!canConfirm && !loading && (
            <p className="text-[11px] text-text-muted">
              {!reasonReady
                ? 'Enter a reason to enable this action.'
                : 'Enter a valid amount to enable this action.'}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || !canConfirm}
            title={
              !reasonReady
                ? 'Enter a reason first'
                : !amountValid
                  ? 'Enter a valid amount first'
                  : undefined
            }
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-warning text-white hover:bg-warning/90 flex items-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
